import { Paper, Flex, Box, Text } from '@mantine/core';
import { createContext, useContext, useState, useEffect } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import styles from './SiestaSummaryCard.module.css';

const SectionExpandedContext = createContext(false);

export function useSectionExpanded() {
  return useContext(SectionExpandedContext);
}

type SummarySection = {
  title: string;
  content: React.ReactNode;
};

type SiestaSummaryCardProps = {
  width?: number;
  title?: string;
  headline: React.ReactNode;
  subline?: React.ReactNode;
  sections?: SummarySection[];
  showDetailsButton?: boolean;
  onAnyExpanded?: (hasAnyExpanded: boolean) => void;
};

export function SiestaSummaryCard({
  width = 374,
  title,
  headline,
  subline,
  sections = [],
  showDetailsButton = false,
  onAnyExpanded,
}: SiestaSummaryCardProps) {
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());

  useEffect(() => {
    setExpandedSections(new Set());
  }, [sections.length]);

  useEffect(() => {
    onAnyExpanded?.(expandedSections.size > 0);
  }, [expandedSections.size, onAnyExpanded]);

  const toggleSection = (index: number) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <Paper
      radius={24}
      p="xxs"
      style={{
        width,
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'rgba(0, 0, 0, 0.25)',
        backdropFilter: 'blur(24px)',
        border: '1px solid rgba(255,255,255,0.15)',
        transition: 'width 300ms ease-in-out',
      }}
    >
      <Flex align="center" direction="column" pb={8} style={{ flexShrink: 0 }}>
        {title && (
          <Text c="white" fw={FONT_WEIGHTS.medium} size="xs" pt={12}>
            {title}
          </Text>
        )}

        {headline}

        {subline && (
          <Text c="white" fw={FONT_WEIGHTS.medium} size="xs" opacity={0.5} pb={12}>
            {subline}
          </Text>
        )}
      </Flex>

      <Box mih={0} pr="xxxs" className={styles.scrollbar} style={{ flex: 1, minHeight: 0 }}>
        {sections.map((section, i) => (
          <Box
            key={i}
            mt={i === 0 ? 0 : 'xs'}
            p="md"
            bg="rgba(0, 0, 0, 0.20)"
            style={{
              borderRadius: 18,
            }}
          >
            <Flex align="center" justify="space-between" mb="xxs">
              <Text c="white" fw={FONT_WEIGHTS.bold} size="sm">
                {section.title}
              </Text>
              {showDetailsButton && (
                <Text
                  c="brand.3"
                  size="sm"
                  fw={FONT_WEIGHTS.bold}
                  onClick={() => toggleSection(i)}
                  style={{ cursor: 'pointer' }}
                >
                  {expandedSections.has(i) ? 'Hide' : 'Details'}
                </Text>
              )}
            </Flex>

            <SectionExpandedContext.Provider value={expandedSections.has(i)}>
              {section.content}
            </SectionExpandedContext.Provider>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}
